/* global SideDrawer */

if (!customElements.get('facet-filters')) {
  class FacetFilters extends SideDrawer {
    connectedCallback() {
      this.init();
    }

    disconnectedCallback() {
      window.removeEventListener('popstate', this.historyChangeHandler);

      if (this.breakpointChangeHandler) {
        window.removeEventListener('on:breakpoint-change', this.breakpointChangeHandler);
      }

      if (this.ccBrandAbortController) {
        this.ccBrandAbortController.abort();
      }
    }

    init() {
      this.filteringEnabled = this.dataset.filtering === 'true';
      this.sortingEnabled = this.dataset.sorting === 'true';
      this.form = document.getElementById('facets');
      this.results = document.getElementById('filter-results');
      this.expanded = [];
      this.filterChangeTimeout = null;

      this.handleBreakpointChange();
      this.addElements();
      this.addListeners();

      this.historyChangeHandler = this.historyChangeHandler || this.handleHistoryChange.bind(this);
      window.addEventListener('popstate', this.historyChangeHandler);

      this.handleCrossCollectionBrands();
    }

    async handleCrossCollectionBrands() {
      const baseUrl = this.dataset.baseCollectionUrl;
      if (!baseUrl || !this.filteringEnabled) return;

      // Skip when already on the base collection; its native filter already shows every brand
      if (this.normalizePath(window.location.pathname) === this.normalizePath(baseUrl)) return;

      const base = await this.getCrossCollectionBrandData(baseUrl);
      if (!base?.items.length) return;

      const activeBrands = new Set(new URLSearchParams(window.location.search).getAll('filter.p.m.info.brand'));
      const maxOptions = Number(this.dataset.maxFilterOptions) || 0;
      const list = this.buildBrandList(base.items, activeBrands, baseUrl, maxOptions);

      // The DOM may have been replaced while the fetch was in flight
      const currentDetails = this.querySelector('details[id="filter-filter.p.m.info.brand"]');
      if (currentDetails) {
        const content = currentDetails.querySelector('.filter__content');
        if (!content) return;
        content.replaceChildren(list);
        this.syncShowMore(currentDetails, base.items.length, maxOptions);
        return;
      }

      // Current collection exposes no Brand facet of its own; inject one built from the base
      const container = this.querySelector('.facets__filters');
      if (!container) return;
      const injected = base.element.cloneNode(true);
      injected.querySelector('.filter__content ul')?.replaceWith(list);
      injected.querySelector('.js-clear-filter')?.remove();
      this.syncShowMore(injected, base.items.length, maxOptions);
      container.prepend(injected);
    }

    /**
     * Fetches and caches the base collection's Brand facet.
     * @param {string} baseUrl - URL of the base collection.
     * @returns {Promise<{element: Element, items: Array}|null>}
     */
    getCrossCollectionBrandData(baseUrl) {
      if (this.ccBrandData !== undefined) return Promise.resolve(this.ccBrandData);
      if (this.ccBrandRequest) return this.ccBrandRequest;

      const sectionId = this.form?.dataset?.filterSectionId;
      if (!sectionId) return Promise.resolve(null);

      this.ccBrandRequest = (async () => {
        const controller = new AbortController();
        this.ccBrandAbortController = controller;
        try {
          const response = await fetch(`${baseUrl}?section_id=${sectionId}`, { signal: controller.signal });
          if (!response.ok) return null;

          const tmpl = document.createElement('template');
          tmpl.innerHTML = await response.text();

          const details = tmpl.content.querySelector('details[id="filter-filter.p.m.info.brand"]');
          const list = details?.querySelector('.filter__content ul');
          if (!details || !list) return null;

          const items = Array.from(list.querySelectorAll('li'))
            .map((li) => {
              const checkbox = li.querySelector('input[type="checkbox"]');
              if (!checkbox) return null;
              return {
                value: checkbox.value,
                label: (li.querySelector('.filter__label .flex-auto')?.textContent || '').trim(),
                count: (li.querySelector('.filter__count')?.textContent || '').trim()
              };
            })
            .filter(Boolean);

          this.ccBrandData = { element: details.closest('details-disclosure') || details, items };
          return this.ccBrandData;
        } catch (error) {
          if (error.name !== 'AbortError') console.warn('Cross-collection brands:', error);
          return null;
        } finally {
          this.ccBrandRequest = null;
        }
      })();

      return this.ccBrandRequest;
    }

    /**
     * Builds the cross-collection Brand list as navigational links.
     * @param {Array} items - Brand items.
     * @param {Set} activeBrands - Currently active brand values.
     * @param {string} baseUrl - Base collection URL.
     * @param {number} maxOptions - Options visible before collapsing.
     * @returns {HTMLUListElement}
     */
    buildBrandList(items, activeBrands, baseUrl, maxOptions) {
      const list = document.createElement('ul');
      list.setAttribute('role', 'list');

      items.forEach((item, index) => {
        const isActive = activeBrands.has(item.value);
        const li = document.createElement('li');
        if (maxOptions && index >= maxOptions) li.classList.add('js-hidden');

        const link = document.createElement('a');
        link.className = `filter__label flex items-start w-full m-0${isActive ? ' active' : ''}`;
        link.href = isActive ? baseUrl : `${baseUrl}?filter.p.m.info.brand=${encodeURIComponent(item.value)}`;
        if (isActive) link.setAttribute('aria-current', 'true');
        if (this.dataset.preloadLinks === 'true') link.dataset.instant = '';

        const label = document.createElement('span');
        label.className = 'flex-auto';
        label.textContent = item.label;
        link.appendChild(label);

        if (item.count) {
          const count = document.createElement('span');
          count.className = 'filter__count';
          count.textContent = item.count;
          link.appendChild(count);
        }

        li.appendChild(link);

        // Keep the active brand in the form payload so AJAX filter changes don't drop it
        if (isActive) {
          const hidden = document.createElement('input');
          hidden.type = 'hidden';
          hidden.name = 'filter.p.m.info.brand';
          hidden.value = item.value;
          li.appendChild(hidden);
        }

        list.appendChild(li);
      });

      // Carry any active brand missing from the base list so it isn't dropped
      const itemValues = new Set(items.map((item) => item.value));
      activeBrands.forEach((value) => {
        if (itemValues.has(value)) return;
        const li = document.createElement('li');
        li.className = 'visually-hidden';
        const hidden = document.createElement('input');
        hidden.type = 'hidden';
        hidden.name = 'filter.p.m.info.brand';
        hidden.value = value;
        li.appendChild(hidden);
        list.appendChild(li);
      });

      return list;
    }

    /**
     * Rebuilds the "Show more" control to match the injected list length.
     * @param {Element} details - The Brand filter element.
     * @param {number} itemCount - Number of brand options.
     * @param {number} maxOptions - Options visible before collapsing.
     */
    syncShowMore(details, itemCount, maxOptions) {
      const wrap = details.querySelector('.disclosure__show-more');
      if (!wrap) return;

      wrap.querySelector('.js-show-more')?.remove();

      if (maxOptions && itemCount > maxOptions) {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'filter__more link text-current text-sm js-show-more';
        button.textContent = this.dataset.showMoreText || 'Show more';
        wrap.prepend(button);
      }
    }

    /**
     * Normalises a path for comparison (strips trailing slashes).
     * @param {string} path
     * @returns {string}
     */
    normalizePath(path) {
      try {
        return new URL(path, window.location.origin).pathname.replace(/\/+$/, '');
      } catch (error) {
        return '';
      }
    }

    addElements() {
      if (this.filteringEnabled) {
        this.filters = this.querySelector('.facets__filters');
        this.activeFilters = this.querySelector('.facets__active-filters');
        this.activeFiltersList = this.querySelector('.active-filters');
        this.activeFiltersHeader = this.querySelector('.active-filters-header');
        this.footer = this.querySelector('.facets__footer');
      }

      if (this.sortingEnabled) {
        this.mobileSortByOptions = this.querySelectorAll('.js-drawer-sort-by');
        this.desktopSortBy = document.querySelector('.products-toolbar__sort');
      }
    }

    addListeners() {
      if (this.filteringEnabled) {
        this.breakpointChangeHandler = this.breakpointChangeHandler
          || this.handleBreakpointChange.bind(this);
        this.filters.addEventListener('click', this.handleFiltersClick.bind(this));
        this.filters.addEventListener('input', this.handleFilterChange.bind(this));
        this.filters.addEventListener('change', this.handleFilterChange.bind(this));
        this.activeFilters.addEventListener('click', this.handleActiveFiltersClick.bind(this));
        window.addEventListener('on:breakpoint-change', this.breakpointChangeHandler);
      }

      if (this.sortingEnabled) {
        this.desktopSortBy.addEventListener('change', this.handleFilterChange.bind(this));
      }
    }

    /**
     * Handles viewport breakpoint changes.
     */
    handleBreakpointChange() {
      if (theme.mediaMatches.lg) {
        this.setAttribute('open', '');
        this.setAttribute('aria-hidden', 'false');
        this.removeAttribute('aria-modal');
        this.removeAttribute('role');
      } else {
        this.close();
        this.setAttribute('role', 'dialog');
        this.setAttribute('aria-modal', 'true');
        this.setAttribute('aria-hidden', 'true');
        this.hidden = false;
      }
    }

    /**
     * Handles 'input' events on the filters and 'change' events on the sort by dropdown.
     * @param {object} evt - Event object.
     */
    handleFilterChange(evt) {
      // Only allow price 'change' events
      if (evt.type === 'change' && !(evt.target.id?.includes('price-range') || evt.target.id?.includes('sort-by'))) return;

      // Dont reload when typing a price
      if (evt.target.id?.includes('price-range') && evt.constructor.name === 'InputEvent') return;

      const timeoutDelay = 500;

      clearTimeout(this.filterChangeTimeout);

      this.filterChangeTimeout = setTimeout(() => {
        const formData = new FormData(this.form);
        const searchParams = new URLSearchParams(formData);
        const emptyParams = [];

        if (this.sortingEnabled) {
          let currentSortBy = searchParams.get('sort_by');

          // Keep the mobile facets form sync'd with the desktop sort by dropdown
          if (evt.target.tagName === 'CUSTOM-SELECT') {
            this.mobileSortByOptions.forEach((option) => {
              option.checked = option.value === evt.detail.selectedValue;
              currentSortBy = evt.detail.selectedValue;
            });
          }

          // Set the 'sort_by' parameter.
          searchParams.set('sort_by', currentSortBy);
        }

        // Get empty parameters.
        searchParams.forEach((value, key) => {
          if (!value) emptyParams.push(key);
        });

        // Remove empty parameters.
        emptyParams.forEach((key) => {
          searchParams.delete(key);
        });

        this.applyFilters(searchParams.toString(), evt);
      }, timeoutDelay);
    }

    /**
     * Handles 'click' events on the filters.
     * @param {object} evt - Event object.
     */
    handleFiltersClick(evt) {
      const { target } = evt;

      // Filter 'clear' button clicked.
      if (target.matches('.js-clear-filter')) {
        evt.preventDefault();
        this.applyFilters(new URL(evt.target.href).searchParams.toString(), evt);
      }

      // Filter 'show more' button clicked.
      if (target.matches('.js-show-more')) {
        const filter = target.closest('.filter');
        target.remove();

        filter.querySelectorAll('li').forEach((el) => {
          el.classList.remove('js-hidden');
        });

        if (!this.expanded.includes(filter.id)) {
          this.expanded.push(filter.id);
        }
      }
    }

    /**
     * Handles 'click' events on the active filters.
     * @param {object} evt - Event object.
     */
    handleActiveFiltersClick(evt) {
      if (evt.target.tagName !== 'A') return;
      evt.preventDefault();
      this.applyFilters(new URL(evt.target.href).searchParams.toString(), evt);
    }

    /**
     * Handles history changes (e.g. back button clicked).
     * @param {object} evt - Event object.
     */
    handleHistoryChange(evt) {
      if (evt.state !== null) {
        let searchParams = '';

        if (evt.state && evt.state.searchParams) {
          ({ searchParams } = evt.state);
        }

        this.applyFilters(searchParams, null, false);
      }
    }

    /**
     * Fetches the filtered/sorted page data and updates the current page.
     * @param {string} searchParams - Filter/sort search parameters.
     * @param {object} evt - Event object.
     * @param {boolean} [updateUrl=true] - Update url with the selected options.
     */
    async applyFilters(searchParams, evt, updateUrl = true) {
      try {
        // Preserve the current element focus
        const activeElementId = document.activeElement.id;

        // Disable infinite scrolling.
        const customPagination = document.querySelector('custom-pagination');
        if (customPagination) customPagination.dataset.pauseInfiniteScroll = 'true';

        // Set loading state.
        this.results.classList.add('is-loading');

        // Disable "Show X results" button until submission is complete.
        const closeBtn = this.querySelector('.js-close-drawer-mob');
        closeBtn.ariaDisabled = 'true';
        closeBtn.classList.add('is-loading');

        // Use Section Rendering API for the request, if possible.
        let fetchUrl = `${window.location.pathname}?${searchParams}`;
        if (this.form.dataset.filterSectionId) {
          fetchUrl += `&section_id=${this.form.dataset.filterSectionId}`;
        }

        // Cancel current fetch request. (Raises an exception)
        if (this.applyFiltersFetchAbortController) {
          this.applyFiltersFetchAbortController.abort('Request changed');
        }
        this.applyFiltersFetchAbortController = new AbortController();

        // Fetch filtered products markup.
        const response = await fetch(fetchUrl, {
          method: 'GET',
          signal: this.applyFiltersFetchAbortController.signal
        });

        if (response.ok) {
          const tmpl = document.createElement('template');
          tmpl.innerHTML = await response.text();

          // Restore UI state.
          this.form.querySelectorAll('details-disclosure > details').forEach((existingFilter) => {
            const target = tmpl.content.getElementById(existingFilter.id);
            if (target) {
              target.open = existingFilter.open;
            }
          });
          tmpl.content.querySelectorAll('#facets details-disclosure > details').forEach((newFilter) => {
            if (this.expanded.includes(newFilter.id)) {
              const hiddenElements = newFilter.querySelectorAll('.js-hidden');
              hiddenElements.forEach((listItem) => {
                listItem.classList.remove('js-hidden');
              });
              newFilter.querySelector('.filter__more')?.remove();
            }
          });

          // Update the filters.
          this.form.innerHTML = tmpl.content.getElementById('facets').innerHTML;

          // Update the label of the mobile filter button
          closeBtn.innerText = tmpl.content.querySelector('.js-close-drawer-mob').innerText;

          // Preserve the CSS class of the results
          const currentResultsUl = this.results.querySelector('ul');
          this.currentResultsClass = currentResultsUl ? this.results.querySelector('ul').getAttribute('class') : this.currentResultsClass;

          // Update the results.
          this.results.innerHTML = tmpl.content.getElementById('filter-results').innerHTML;

          // Set the CSS class of the results to what it was
          const newResultsUl = this.results.querySelector('ul');
          if (newResultsUl && this.currentResultsClass) newResultsUl.setAttribute('class', this.currentResultsClass);

          // Reinitialize re-rendered components.
          this.addElements();
          this.addListeners();
          this.handleCrossCollectionBrands();

          // Reinitialize any custom pagination
          if (customPagination && customPagination.reload) customPagination.reload();

          // Update the URL.
          if (updateUrl) FacetFilters.updateURL(searchParams);

          // Scroll to the top of the results if needed
          if (this.results.getBoundingClientRect().top < 0) {
            // If the header is sticky, compensate for it when scrolling to elements
            let headerHeight = 0;
            if (document.querySelector('store-header[data-is-sticky="true"]')) {
              headerHeight = Number.parseInt(
                getComputedStyle(this.parentElement)
                  .getPropertyValue('--header-height')
                  .replace('px', ''),
                10
              ) || 0;
            }
            window.scrollTo({
              top: this.results.getBoundingClientRect().top + window.scrollY - headerHeight - 45,
              behavior: 'smooth'
            });
          }

          // Enable the "Show X results" button
          closeBtn.classList.remove('is-loading');
          closeBtn.removeAttribute('aria-disabled');

          // Renable infinite scroll
          if (customPagination) customPagination.dataset.pauseInfiniteScroll = 'false';

          // Focus on the element with the same ID in the new HTML
          if (activeElementId) document.getElementById(activeElementId)?.focus();

          // Broadcast the update for anything else to hook into
          document.dispatchEvent(new CustomEvent('on:facet-filters:updated'), { bubbles: true });
        }
      } catch (error) {
        console.warn(error); // eslint-disable-line
      } finally {
        this.results.classList.remove('is-loading');
      }
    }

    /**
     * Updates the url with the current filter/sort parameters.
     * @param {string} searchParams - Filter/sort parameters.
     */
    static updateURL(searchParams) {
      window.history.pushState(
        { searchParams },
        '',
        `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`
      );
    }
  }

  customElements.define('facet-filters', FacetFilters);
}
